/**
 * iOS-stijl Ja/Nee bevestigingsmodal — werkt zonder React-context (vanilla DOM).
 *
 * Gebruik:
 *   const ja = await iosConfirm({
 *     titel: "MET prijzen?",
 *     bericht: "Wil je dit document afdrukken met prijzen?",
 *     ja: "Ja, met prijzen",
 *     nee: "Nee, zonder prijzen",
 *     icon: "💶",
 *   });
 *   if (ja) { ... } else { ... }
 *
 * Returns:
 *   - true  → gebruiker klikte "Ja"
 *   - false → gebruiker klikte "Nee" of sloot de modal
 */
export function iosConfirm({ titel = 'Bevestigen', bericht = '', ja = 'Ja', nee = 'Nee', icon = '' } = {}) {
  return new Promise((resolve) => {
    // Bouw DOM
    const overlay = document.createElement('div');
    overlay.className = 'ios-confirm-overlay';
    Object.assign(overlay.style, {
      position: 'fixed', inset: '0', zIndex: '9999',
      background: 'radial-gradient(ellipse at top, rgba(15,23,42,0.55) 0%, rgba(0,0,0,0.7) 100%)',
      backdropFilter: 'blur(12px) saturate(140%)', WebkitBackdropFilter: 'blur(12px) saturate(140%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px', opacity: '0', transition: 'opacity 0.25s ease',
    });

    const card = document.createElement('div');
    Object.assign(card.style, {
      background: 'rgba(255,255,255,0.95)',
      borderRadius: '24px',
      width: '100%', maxWidth: '360px',
      overflow: 'hidden',
      boxShadow: '0 30px 90px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.6) inset',
      transform: 'scale(0.88) translateY(20px)',
      transition: 'transform 0.32s cubic-bezier(.34,1.56,.64,1)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", system-ui, sans-serif',
      backdropFilter: 'blur(20px)',
    });

    const body = document.createElement('div');
    Object.assign(body.style, {
      padding: '32px 28px 24px', textAlign: 'center',
    });
    body.innerHTML = `
      ${icon ? `<div style="width:72px;height:72px;margin:0 auto 18px;background:linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);border-radius:20px;display:flex;align-items:center;justify-content:center;font-size:38px;line-height:1;box-shadow:0 6px 20px rgba(59,130,246,0.18), inset 0 1px 0 rgba(255,255,255,0.8)">${icon}</div>` : ''}
      <div style="font-size:20px;font-weight:700;color:#0F172A;margin-bottom:8px;letter-spacing:-0.025em;line-height:1.2">${escapeHtml(titel)}</div>
      <div style="font-size:14px;line-height:1.5;color:#64748B;letter-spacing:-0.005em;white-space:pre-wrap;max-width:300px;margin:0 auto">${escapeHtml(bericht)}</div>
    `;

    const actions = document.createElement('div');
    Object.assign(actions.style, {
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px',
      padding: '0 20px 22px',
    });

    const btnNo = document.createElement('button');
    btnNo.type = 'button';
    btnNo.textContent = nee;
    btnNo.setAttribute('data-testid', 'ios-confirm-nee');
    Object.assign(btnNo.style, {
      padding: '14px 8px',
      background: 'rgba(241,245,249,0.95)',
      border: '1px solid rgba(203,213,225,0.6)',
      borderRadius: '14px',
      fontSize: '15px', fontWeight: '600', color: '#475569',
      cursor: 'pointer', letterSpacing: '-0.01em',
      transition: 'all 0.18s ease',
      fontFamily: 'inherit',
    });
    btnNo.onmouseenter = () => {
      btnNo.style.background = 'rgba(226,232,240,0.95)';
      btnNo.style.transform = 'translateY(-1px)';
      btnNo.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
    };
    btnNo.onmouseleave = () => {
      btnNo.style.background = 'rgba(241,245,249,0.95)';
      btnNo.style.transform = 'translateY(0)';
      btnNo.style.boxShadow = 'none';
    };

    const btnYes = document.createElement('button');
    btnYes.type = 'button';
    btnYes.textContent = ja;
    btnYes.setAttribute('data-testid', 'ios-confirm-ja');
    Object.assign(btnYes.style, {
      padding: '14px 8px',
      background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
      border: '0',
      borderRadius: '14px',
      fontSize: '15px', fontWeight: '700', color: '#FFFFFF',
      cursor: 'pointer', letterSpacing: '-0.01em',
      transition: 'all 0.18s ease',
      fontFamily: 'inherit',
      boxShadow: '0 4px 14px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
    });
    btnYes.onmouseenter = () => {
      btnYes.style.background = 'linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%)';
      btnYes.style.transform = 'translateY(-1px)';
      btnYes.style.boxShadow = '0 8px 22px rgba(37,99,235,0.45), inset 0 1px 0 rgba(255,255,255,0.2)';
    };
    btnYes.onmouseleave = () => {
      btnYes.style.background = 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)';
      btnYes.style.transform = 'translateY(0)';
      btnYes.style.boxShadow = '0 4px 14px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.2)';
    };

    actions.appendChild(btnNo);
    actions.appendChild(btnYes);
    card.appendChild(body);
    card.appendChild(actions);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    // Animeer in
    requestAnimationFrame(() => {
      overlay.style.opacity = '1';
      card.style.transform = 'scale(1) translateY(0)';
    });

    const close = (result) => {
      overlay.style.opacity = '0';
      card.style.transform = 'scale(0.92) translateY(10px)';
      setTimeout(() => {
        try { document.body.removeChild(overlay); } catch (_e) { /* niet kritiek */ }
        document.removeEventListener('keydown', onKey);
        resolve(result);
      }, 220);
    };
    btnYes.onclick = () => close(true);
    btnNo.onclick = () => close(false);
    overlay.onclick = (e) => { if (e.target === overlay) close(false); };

    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    document.addEventListener('keydown', onKey);
  });
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}
